import Icon1 from "@assets/icons/Icon";
import React, { useEffect, useState, useRef, ReactNode, JSX } from "react";
import { colors } from "sexy";

interface SearchOption {
  label: string;
  value: string;
  extra_label?: string;
  extra_label_2?: string;
}

interface SearchAlgoliaProps {
  value?: string;
  setValue: (value: string) => void;
  options?: SearchOption[];
  placeholder?: string;
  onAction: (option: SearchOption | string) => boolean | void;
  classNameCustom?: string;
  start_only?: boolean;
  visible_empty?: boolean;
  show_options?: boolean;
  debounceDelay?: number;
  textfield2t_mode?: boolean;
  focus_on_start?: boolean;
  children?: ReactNode;
  navigateWorkspace?: (path: string) => void;
}

const SearchAlgolia: React.FC<SearchAlgoliaProps> = ({
  value = "",
  setValue,
  options = [],
  placeholder = "Rechercher...",
  onAction,
  classNameCustom = "w-400",
  start_only = true,
  visible_empty = false,
  show_options = true,
  debounceDelay = 1000,
  textfield2t_mode = false,
  focus_on_start = false,
  children = null,
  navigateWorkspace = () => {},
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [localInputValue, setLocalInputValue] = useState<string>(value);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const isLoading = useRef<boolean>(false);
  const [focus, setFocus] = useState<boolean>(false);
  const basrRef = useRef<HTMLDivElement>(null);
  const rect = basrRef.current?.getBoundingClientRect();
  const width = rect ? rect.width : 0;
  // Sync local value with external value when it changes externally
  useEffect(() => {
    setLocalInputValue(value);
  }, [value]);

  // Debounced effect to update external value
  useEffect(() => {
    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Only debounce if there's actually a change and the component is mounted
    if (localInputValue !== value) {
      isLoading.current = true;

      debounceRef.current = setTimeout(() => {
        setValue(localInputValue);
        isLoading.current = false;
      }, debounceDelay);
    }

    // Cleanup timeout on unmount
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [localInputValue, value, setValue, debounceDelay]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalInputValue(value);

    // Show/hide dropdown based on local value for immediate UI feedback
    if (value) {
      setIsDropdownVisible(true);
    } else {
      setIsDropdownVisible(false);
    }
  };

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Update the position when focus changes or on mount
  useEffect(() => {
    if (focus && basrRef.current) {
      const rect = basrRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY - 40,
        left: rect.left + window.scrollX,
      });
    }
  }, [focus]);

  const resetSearch = () => {
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    isLoading.current = false;

    if (visible_empty) {
      setIsDropdownVisible(true);
      setLocalInputValue("");
      setValue("");
    } else {
      setLocalInputValue("");
      setValue("");
      setIsDropdownVisible(false);
      const option: SearchOption = { label: "", value: "" };
      onAction(option);
    }
  };

  const handleOptionClick = (
    e: React.MouseEvent<HTMLLIElement>,
    option: SearchOption,
  ) => {
    console.log("clicked option", option);
    e.stopPropagation();

    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    isLoading.current = false;
    setLocalInputValue(option.label);
    setValue(option.label);
    const navigated = onAction(option);
    setIsDropdownVisible(false);
    if (!navigated) {
      try {
        navigateWorkspace(`/folder/${option.value}`);
      } catch (error) {
        console.error("Navigation error:", error);
      }
    }
    setFocus(false);
    setTimeout(() => setIsDropdownVisible(false), 150);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // Clear debounce and immediately update
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      isLoading.current = false;
      setValue(localInputValue);
      setIsDropdownVisible(false);

      if (!show_options) {
        onAction("enter");
      }
    }
  };

  return (
    <div ref={basrRef} className="relative h-10 ">
      {focus && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setFocus(false);
            setTimeout(() => setIsDropdownVisible(false), 150);
          }}
          className="fixed top-0 left-0 w-full h-full bg-shadow_25 z-10"
        ></div>
      )}
      <div
        style={{
          width: width ? width : "auto",
          top: dropdownPosition.top,
          left: dropdownPosition.left,
        }}
        className={`
        ${focus ? " z-20 ring-4 fixed  ring-neutral-400 " : ""}
        ${classNameCustom}
        h-11 flex gap-2 select-none rounded-xl shadow-sm bg-white border text-neutral-400`}
      >
        <div className="flex flex-row w-full items-center">
          <svg
            className="w-5 ml-4 h-5 text-neutral-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>

          <input
            type="text"
            value={localInputValue}
            onFocus={() => {
              setFocus(true);
              visible_empty && setIsDropdownVisible(true);
            }}
            autoFocus={focus_on_start}
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="w-full px-4 py-2 text-h2 border-none text-black outline-none bg-transparent"
          />
        </div>

        {isDropdownVisible && show_options && (
          <ul className="absolute z-20 w-fit scroll-ba min-w-300 mt-12 py-2 pr-6 bg-white rounded-xl shadow-xl max-h-300 shadow-shadow_10 border scrollbar-none overflow-y-auto">
            {isLoading?.current ? (
              <li className="px-4 py-2 text-h2 text-neutral-500 flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-neutral-400 border-t-transparent rounded-full"></div>
                Recherche en cours...
              </li>
            ) : options.length > 0 ? (
              options.map((option, index) => (
                <li
                  key={index}
                  onClick={(e) => handleOptionClick(e, option)}
                  className="px-4 py-2 cursor-pointer hover:bg-neutral-50 flex flex-row gap-2"
                >
                  <div className="text-h3 flex items-center gap-4">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {renderHighlightedText(
                      option.label +
                        (option.extra_label ? ` - ${option.extra_label}` : "") +
                        (option.extra_label_2
                          ? ` - ${option.extra_label_2}`
                          : ""),
                      localInputValue,
                    )}
                  </div>
                </li>
              ))
            ) : (
              localInputValue && (
                <li className="px-4 py-1 text-h2 text-neutral-400">
                  Aucun résultat trouvé
                </li>
              )
            )}
          </ul>
        )}

        <div
          className={`${
            localInputValue ? "text-black" : "text-white"
          } flex cursor-pointer min-h-full text-h3 items-center pr-4 justify-center`}
          onClick={resetSearch}
        >
          <Icon1 name="effacer2" size={20} color={colors.neutral_400} />
        </div>
        {children && (
          <div className="flex items-center justify-center">{children}</div>
        )}
      </div>
    </div>
  );
};

export default SearchAlgolia;

export const renderHighlightedText = (
  text: string,
  searchValue: string,
): JSX.Element => {
  if (!searchValue.trim()) {
    return <span className="text-gray-500">{text}</span>;
  }

  const searchLower = searchValue.toLowerCase();
  const textLower = text.toLowerCase();
  const matchIndex = textLower.indexOf(searchLower);

  if (matchIndex === -1) {
    return <span className="text-gray-500">{text}</span>;
  }

  const beforeMatch = text.substring(0, matchIndex);
  const match = text.substring(matchIndex, matchIndex + searchValue.length);
  const afterMatch = text.substring(matchIndex + searchValue.length);

  return (
    <span>
      <span className="text-gray-400">{beforeMatch}</span>
      <span className="text-black font-medium">{match}</span>
      <span className="text-gray-400">{afterMatch}</span>
    </span>
  );
};
